import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Radio, ShieldCheck, MapPin, Banknote } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { RadarCanvas } from "@/components/RadarCanvas";
import { Marquee } from "@/components/Marquee";
import QuickSelector from "@/components/QuickSelector";
import Manifesto from "@/components/Manifesto";
import TrustBadges from "@/components/TrustBadges";
import ReviewsSection from "@/components/ReviewsSection";
import AreaCities from "@/components/AreaCities";
import CtaBand from "@/components/CtaBand";
import { Reveal, SectionHead } from "@/components/Reveal";
import { PHONE_DISPLAY, TEL_LINK, waLink, smsLink, DEFAULT_MSG, IMAGES, SERVICES } from "@/constants/site";

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden bg-asphalt grain" data-testid="hero-section">
      <RadarCanvas className="absolute inset-0 h-full w-full opacity-80" />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(60% 50% at 70% 40%, rgba(200,16,46,0.16), transparent 70%)" }}
      />

      <motion.div style={{ opacity: fade }} className="relative mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 pt-16 pb-24 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pt-24">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex items-center gap-2.5 border border-dispatch/50 bg-dispatch/10 px-4 py-2"
            data-testid="hero-dispatch-status-badge"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-dispatch opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-dispatch" />
            </span>
            <span className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-dispatch">
              Dispatch Active — 24/7
            </span>
          </motion.div>

          <h1 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[1.02] tracking-tight text-white" data-testid="hero-headline">
            <Line delay={0.25}>Fast, Reliable</Line>
            <Line delay={0.38} className="text-emergency">Light-Duty Towing</Line>
            <Line delay={0.51}><span className="text-outline">In Tarrant County, TX</span></Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-6 max-w-lg text-lg text-zinc-400"
            data-testid="hero-subheadline"
          >
            Stranded? We'll be there in no time. One call or text and a truck is rolling —
            anywhere in Tarrant County, day or night.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <a
              href={TEL_LINK}
              data-testid="hero-call-button"
              className="pulse-ring inline-flex items-center justify-center gap-2.5 bg-emergency px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-emergency-dark hover:-translate-y-0.5"
            >
              <Phone className="h-5 w-5" /> Call Now {PHONE_DISPLAY}
            </a>
            <a
              href={waLink(DEFAULT_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="pulse-ring-green inline-flex items-center justify-center gap-2.5 bg-dispatch px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-dispatch-dark hover:-translate-y-0.5"
            >
              <FaWhatsapp className="h-5 w-5" /> Text or WhatsApp for Faster Response
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2"
          >
            {[
              { icon: Radio, label: "Avg. response ~18 min" },
              { icon: ShieldCheck, label: "Licensed & Insured" },
              { icon: Banknote, label: "No hidden fees" },
            ].map((c) => (
              <span key={c.label} className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-500">
                <c.icon className="h-3.5 w-3.5 text-emergency" /> {c.label}
              </span>
            ))}
            <a href={smsLink(DEFAULT_MSG)} data-testid="hero-sms-link" className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-400 underline underline-offset-4 hover:text-white transition-colors">
              or send a plain text
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
        >
          <div className="absolute -inset-6 rounded-full blur-3xl" style={{ background: "radial-gradient(closest-side, rgba(200,16,46,0.25), transparent)" }} />
          <motion.div style={{ y: imgY }} className="clip-frame relative border-2 border-emergency/60">
            <img
              src={IMAGES.hero}
              alt="Tow truck operating on the highway"
              className="h-[480px] w-full object-cover"
              data-testid="hero-truck-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-asphalt/80 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 flex items-center gap-2 bg-charcoal/90 px-4 py-2.5 backdrop-blur">
              <MapPin className="h-4 w-4 text-emergency" />
              <span className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-white">
                Unit 01 — On Patrol, Fort Worth
              </span>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

const ServicesPreview = () => (
  <section className="bg-white py-20 sm:py-28" data-testid="services-preview-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <SectionHead
        eyebrow="What We Do"
        title="Services built for the roadside"
        sub="From a dead battery in a parking lot to a full winch-out — one number handles it all."
      />
      <div className="grid gap-5 md:grid-cols-3">
        {SERVICES.slice(0, 3).map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08}>
            <a href="/services" className="group block h-full border border-zinc-200 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:border-emergency hover:shadow-[0_24px_60px_-24px_rgba(200,16,46,0.35)]" data-testid={`home-service-${s.id}`}>
              <div className="relative h-44 overflow-hidden bg-charcoal">
                <img src={s.image} alt={s.title} className={`h-full w-full transition-transform duration-700 group-hover:scale-105 ${s.fit === "contain" ? "object-contain" : "object-cover"}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 to-transparent" />
                <s.icon className="absolute bottom-4 left-4 h-7 w-7 text-white" strokeWidth={1.8} />
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl sm:text-2xl font-bold uppercase text-zinc-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{s.desc}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.features.slice(0, 2).map((f) => (
                    <span key={f} className="border border-zinc-200 px-2.5 py-1 font-mono text-[10px] tracking-wider uppercase text-zinc-500">{f}</span>
                  ))}
                </div>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <QuickSelector />
    <ServicesPreview />
    <Manifesto />
    <TrustBadges />
    <ReviewsSection />
    <AreaCities />
    <CtaBand />
  </>
);

export default Home;
